import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import StatusChip from "../../../../components/common/StatusChip";
import SwitchButton from "../../../../components/SwitchButton";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { roleService } from "../../../user-management/services";
import { tenantRoleService } from "../../../role-management/services";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";
import { CLIENT_SCOPES } from "../../constants";
import { systemClientService, tenantClientService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import { filterByCatalog } from "../../../../helpers/catalogFilter.helper";
import {
  matchesClientSearch,
  normalizeClientList,
} from "../../utils/normalizeClient";
import ClientFormDialog from "../ClientFormDialog";

const ClientManagement = ({ scope = CLIENT_SCOPES.SYSTEM }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeTenantId, tenantId, accessToken } = useAuth();
  const isSystem = scope === CLIENT_SCOPES.SYSTEM;
  const requestTenantId = activeTenantId || tenantId;
  const listBase = isSystem ? "/clients" : "/tenant-clients";

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");

  const [createOpen, setCreateOpen] = useState(false);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enableLoadingId, setEnableLoadingId] = useState("");
  const [pendingEnabled, setPendingEnabled] = useState({});

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      if (isSystem) {
        const roleList = await roleService.getAllRoles();
        setRoles(roleList);
      } else {
        const payload = await tenantRoleService.getAllTenantRoles(
          requestTenantId,
          accessToken
        );
        setRoles(
          normalizeRoleList(payload)
            .map((role) => String(role.name || "").trim())
            .filter(Boolean)
        );
      }
    } catch {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [accessToken, isSystem, requestTenantId]);

  const loadClients = useCallback(async (name = appliedName) => {
    setLoading(true);
    setError("");
    try {
      const payload = isSystem
        ? await systemClientService.getAllSystemClients()
        : await tenantClientService.getAllTenantClients(
            requestTenantId,
            accessToken
          );
      const normalized = normalizeClientList(payload).filter((client) =>
        matchesClientSearch(client, { name })
      );
      setClients(normalized);
    } catch (err) {
      const message = getApiErrorMessage(err);
      setClients([]);
      setError(message);
      dispatch(appActions.apiErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [accessToken, appliedName, dispatch, isSystem, requestTenantId]);

  useEffect(() => {
    loadClients(appliedName);
  }, [appliedName, loadClients]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const openDetails = useCallback(
    (row) => {
      const id = row?.clientId || row?.id;
      if (!id) return;
      navigate(`${listBase}/view/${encodeURIComponent(id)}`, {
        state: { client: row },
      });
    },
    [listBase, navigate]
  );

  const openCreate = async () => {
    setCreateOpen(true);
    setRoles([]);
    await loadRoles();
  };

  const isClientEnabled = useCallback(
    (row = {}) => {
      const clientId = row.clientId || row.id;
      if (clientId && clientId in pendingEnabled) {
        return Boolean(pendingEnabled[clientId]);
      }
      return row.enabled !== false;
    },
    [pendingEnabled]
  );

  const handleEnableDisable = useCallback(
    async (row, enabled) => {
      const clientId = row?.clientId || row?.id;
      // System client collection has no enable/disable API.
      if (isSystem || !clientId || enableLoadingId) return;

      setPendingEnabled((prev) => ({ ...prev, [clientId]: enabled }));
      setEnableLoadingId(clientId);
      try {
        await tenantClientService.updateClientEnabled(
          clientId,
          enabled,
          accessToken,
          requestTenantId
        );
        dispatch(
          appActions.apiSuccessMessage(
            enabled ? "Client enabled successfully." : "Client disabled successfully."
          )
        );
        setClients((prev) =>
          prev.map((client) =>
            (client.clientId || client.id) === clientId
              ? { ...client, enabled }
              : client
          )
        );
        setPendingEnabled((prev) => {
          const next = { ...prev };
          delete next[clientId];
          return next;
        });
      } catch (err) {
        setPendingEnabled((prev) => {
          const next = { ...prev };
          delete next[clientId];
          return next;
        });
        dispatch(
          appActions.apiErrorMessage(
            getApiErrorMessage(err) || "Failed to update client status."
          )
        );
      } finally {
        setEnableLoadingId("");
      }
    },
    [accessToken, dispatch, enableLoadingId, isSystem, requestTenantId]
  );

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      if (isSystem) {
        await systemClientService.createSystemClient(payload);
      } else {
        await tenantClientService.createTenantClient(
          payload,
          requestTenantId,
          accessToken
        );
      }
      dispatch(appActions.apiSuccessMessage("Client created successfully."));
      setCreateOpen(false);
      await loadClients(appliedName);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Client Name",
        flex: 1.1,
        minWidth: 160,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "grantType",
        headerName: "Grant",
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.grantType) ? row.grantType.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.grantType} maxVisible={1} />
        ),
      },
      {
        field: "roles",
        headerName: "Roles",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        valueGetter: (value, row) => {
          const items = filterByCatalog(row?.roles, roles);
          return items.join(", ");
        },
        renderCell: (params) => (
          <ChipList
            items={filterByCatalog(params.row.roles, roles)}
            maxVisible={1}
          />
        ),
      },
      {
        field: "enabled",
        headerName: "Status",
        width: isSystem ? 120 : 170,
        sortable: false,
        renderCell: (params) => {
          const enabled = isClientEnabled(params.row);
          const clientId = params.row.clientId || params.row.id;
          // System client APIs have no enable/disable — show status only.
          if (isSystem) {
            return <StatusChip enabled={enabled} />;
          }
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <StatusChip enabled={enabled} />
              <SwitchButton
                checked={enabled}
                disabled={enableLoadingId === clientId}
                tooltipText={enabled ? "Disable client" : "Enable client"}
                onToggle={(nextEnabled) =>
                  handleEnableDisable(params.row, nextEnabled)
                }
              />
            </Stack>
          );
        },
      },
      {
        field: "accessOptions",
        headerName: "Access Options",
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row || {};
          const items = [
            {
              key: "allowUserCreation",
              label: "Allow User Creation",
              checked: Boolean(row.allowUserCreation),
            },
            ...(!isSystem
              ? [
                  {
                    key: "allowSelfUserCreation",
                    label: "Allow Self User Registration",
                    checked: Boolean(row.allowSelfUserCreation),
                  },
                  {
                    key: "allowLoginViaOTP",
                    label: "Allow Login via OTP",
                    checked: Boolean(row.allowLoginViaOTP),
                  },
                ]
              : []),
          ];

          return (
            <Stack spacing={0} sx={{ py: 0.5, width: "100%" }}>
              {items.map((item) => (
                <FormControlLabel
                  key={item.key}
                  sx={{
                    m: 0,
                    mr: 0,
                    alignItems: "center",
                    height: 24,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 12,
                      lineHeight: 1.2,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={item.checked}
                      disabled
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={item.label}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ActionIconButton
            title="View Details"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() => openDetails(params.row)}
          />
        ),
      },
    ],
    [
      enableLoadingId,
      handleEnableDisable,
      isClientEnabled,
      isSystem,
      openDetails,
      roles,
    ]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="Clients"
          subtitle={
            isSystem
              ? "Manage system OAuth clients."
              : "Manage tenant OAuth clients."
          }
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            Add Client
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadClients(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Client Name",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !clients.length ? (
          <PageLoader text="Loading clients..." />
        ) : !loading && !clients.length ? (
          <EmptyState
            title="No clients found"
            description="No clients found for the current filters."
          />
        ) : (
          <DataTable
            rows={clients}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            rowHeight={isSystem ? 52 : 96}
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            getRowId={(row) => row.clientId || row.id || row.name}
            paginationMode="client"
          />
        )}
      </Paper>

      <ClientFormDialog
        open={createOpen}
        mode="create"
        roles={roles}
        rolesLoading={rolesLoading}
        loading={saving}
        showExtendedFields={!isSystem}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

export default ClientManagement;
